import { useEffect, useState } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { adminApi, Card, StatTile, Badge } from './_shared';
import api from '@/lib/api';
import { Activity, Users, FolderKanban, Mail, Sparkles, Plug, Loader2, RefreshCw, KeyRound, ShieldCheck, Clock3 } from 'lucide-react';

export default function AdminOverview() {
  const [data, setData] = useState(null);
  const [licMetrics, setLicMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [ov, lm] = await Promise.all([
        adminApi.overview(),
        api.get('/admin/licences/metrics').then(r => r.data).catch(() => null),
      ]);
      setData(ov);
      setLicMetrics(lm);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <AdminLayout title="System Overview" subtitle="Admin · Dashboard"
      actions={<button onClick={load} className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-slate-400 hover:text-slate-100 px-3 py-1.5 rounded transition">
        <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`}/> Refresh
      </button>}>
      {loading && !data ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-500"/></div>
      ) : data ? (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="overview-stats">
            <StatTile label="Total Users"        value={data.users.total}    sub={`${data.users.active} active · ${data.users.admins} admins`}/>
            <StatTile label="Active Projects"    value={data.projects.active} sub={`${data.projects.archived} archived · ${data.projects.total} total`} accent="orange"/>
            <StatTile label="Audit Events 7-Day" value={data.activity.audit_events_7d} sub={`${data.activity.invites_7d} invites in 7 days`} accent="blue"/>
            <StatTile label="Integrations"
              value={Object.values(data.integrations).filter(Boolean).length + '/3'}
              sub="LLM · Email · 3D Viewer"
              accent="green"/>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Integration health */}
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-5">
                <Plug className="w-4 h-4 text-[#F26B1F]"/>
                <h3 className="text-sm tracking-[0.15em] uppercase text-slate-500">Integration Health</h3>
              </div>
              <div className="space-y-3">
                {[
                  ['Cognara AI Engine',    data.integrations.anthropic_configured],
                  ['Email',           data.integrations.resend_configured],
                  ['3D Viewer',       data.integrations.aps_configured],
                ].map(([name, ok]) => (
                  <div key={name} className="flex items-center justify-between border border-slate-200 rounded-md px-4 py-3">
                    <span className="text-sm text-slate-700">{name}</span>
                    <Badge tone={ok ? 'green' : 'red'}>{ok ? 'Configured' : 'Missing'}</Badge>
                  </div>
                ))}
              </div>
            </Card>

            {/* Customer Licences */}
            <Card className="p-6" data-testid="licence-metrics-widget">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-[#F26B1F]"/>
                  <h3 className="text-sm tracking-[0.15em] uppercase text-slate-500">Customer Licences</h3>
                </div>
                <a href="/admin/licences" className="text-[10px] tracking-[0.2em] uppercase text-[#F26B1F] hover:text-[#B24D0F]">Manage →</a>
              </div>
              {licMetrics ? (
                <>
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div>
                      <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500">Active</div>
                      <div className="text-2xl font-light text-emerald-600 tabular-nums">{licMetrics.active_licences}</div>
                      <div className="text-[10px] text-slate-500 mt-0.5">{licMetrics.pending} pending</div>
                    </div>
                    <div>
                      <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500">Seats</div>
                      <div className="text-2xl font-light text-slate-800 tabular-nums">{licMetrics.active_seats}</div>
                      <div className="text-[10px] text-slate-500 mt-0.5">of {licMetrics.total_seats}</div>
                    </div>
                    <div>
                      <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500">≤ 30 d</div>
                      <div className="text-2xl font-light text-[#F26B1F] tabular-nums">{licMetrics.near_expiry_30d}</div>
                      <div className="text-[10px] text-slate-500 mt-0.5">near expiry</div>
                    </div>
                  </div>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-2">Recent heartbeats</div>
                  {(licMetrics.recent_heartbeats || []).length === 0 ? (
                    <p className="text-xs text-slate-500">No appliance heartbeats yet.</p>
                  ) : (
                    <ul className="space-y-1.5 text-[12px]">
                      {licMetrics.recent_heartbeats.map((h, i) => (
                        <li key={i} className="flex items-center justify-between border-b border-slate-100 pb-1">
                          <span className="text-slate-700 truncate">{h.customer}</span>
                          <span className="text-[10px] text-slate-500 tabular-nums whitespace-nowrap">{(h.ts || '').slice(0, 16).replace('T', ' ')}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              ) : (
                <p className="text-sm text-slate-500">No licences issued yet.</p>
              )}
            </Card>
          </div>

          {/* Recent logins */}
          <Card className="p-6 mt-6">
            <div className="flex items-center gap-2 mb-5">
              <Activity className="w-4 h-4 text-[#1E4E8C]"/>
              <h3 className="text-sm tracking-[0.15em] uppercase text-slate-500">Recent Logins</h3>
            </div>
            {data.recent_logins.length === 0 ? (
              <p className="text-sm text-slate-500">No recent login activity recorded yet. Audit trail will populate as users sign in.</p>
            ) : (
              <ul className="space-y-2 text-[13px]">
                {data.recent_logins.map((l, i) => (
                  <li key={i} className="flex items-center justify-between border-b border-slate-100 py-2">
                    <span className="text-slate-700">{l.actor_email}</span>
                    <span className="text-[11px] text-slate-500 tabular-nums">{(l.ts || '').slice(0, 19).replace('T', ' ')}</span>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {/* Quick links */}
          <Card className="p-6 mt-6">
            <div className="flex items-center gap-2 mb-5">
              <Sparkles className="w-4 h-4 text-[#F26B1F]"/>
              <h3 className="text-sm tracking-[0.15em] uppercase text-slate-500">Common Admin Actions</h3>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                { to: '/admin/users',          icon: Users,        label: 'Manage users' },
                { to: '/admin/projects',       icon: FolderKanban, label: 'Manage projects' },
                { to: '/admin/licences',       icon: KeyRound,     label: 'Issue customer licence', accent: true },
                { to: '/admin/audit',          icon: Activity,     label: 'Open audit log' },
              ].map(it => (
                <a key={it.to} href={it.to} className={`flex items-center gap-3 px-4 py-3 rounded-md border transition group ${it.accent ? 'border-[#F26B1F]/50 bg-[#F26B1F]/5 hover:border-[#F26B1F] hover:bg-[#F26B1F]/10' : 'border-slate-200 hover:border-[#1E4E8C] hover:bg-slate-50'}`}>
                  <it.icon className={`w-4 h-4 transition ${it.accent ? 'text-[#F26B1F]' : 'text-slate-500 group-hover:text-[#F26B1F]'}`}/>
                  <span className={`text-sm ${it.accent ? 'text-[#B24D0F] font-medium' : 'text-slate-700 group-hover:text-slate-900'}`}>{it.label}</span>
                </a>
              ))}
            </div>
          </Card>
        </>
      ) : null}
    </AdminLayout>
  );
}
